import UploadOutlined from '@ant-design/icons/UploadOutlined'
import { Upload } from 'antd'
import type { UploadChangeParam, UploadFile } from 'antd/es/upload'
import { validateFile } from 'features/files/utils/fileUploadUtils'
import { type FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const { Dragger } = Upload

interface FileUploadDraggerProps {
  uploading?: boolean
  fileList: ReadonlyArray<UploadFile>
  onFileListChange: (fileList: ReadonlyArray<UploadFile>) => void
  maxFileSize?: number
  acceptedFileTypes?: ReadonlyArray<string>
  multiple?: boolean
  directory?: boolean
  disabled?: boolean
  className?: string
  condensed?: boolean
  showUploadList?: boolean
}

const StyledDragger = styled(Dragger)<{
  $condensed: boolean
}>`
  .ant-upload-drag {
    border: 0.125rem dashed ${CantileverTheme.colors.gray['600']};
    border-radius: 0.5rem;
    background: ${CantileverTheme.colors.gray['600']};
    padding: ${({ $condensed }) => ($condensed ? '0rem' : '2rem 1rem')};
    text-align: center;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
      border-color: ${CantileverTheme.colors.grayscale[400]};
      background: ${CantileverTheme.colors.grayscale[200]};
    }

    &.ant-upload-drag-hover {
      border-color: ${CantileverTheme.colors.grayscale[400]};
      background: ${CantileverTheme.colors.grayscale[200]};
    }
  }

  .ant-upload-drag-container {
    padding: 0;
  }

  .ant-upload {
    padding: ${({ $condensed }) => ($condensed ? '0' : '16px')} !important;
  }
`

const UploadIcon = styled.div<{ $condensed: boolean }>`
  font-size: 3rem;
  color: ${CantileverTheme.colors.grayscale[500]};
  margin-bottom: ${({ $condensed }) => ($condensed ? '0rem' : '1rem')};
`

const UploadText = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${CantileverTheme.colors.grayscale[800]};
  margin-bottom: 0.5rem;
`

const UploadHint = styled.div`
  font-size: 0.875rem;
  color: ${CantileverTheme.colors.grayscale[500]};
`

/**
 * A dragger component for file uploads that handles file selection and validation
 */
export const FileUploadDragger: FC<FileUploadDraggerProps> = ({
  uploading = false,
  fileList,
  onFileListChange,
  maxFileSize = 20,
  acceptedFileTypes = ['*/*'],
  multiple = true,
  directory = false,
  disabled,
  condensed = false,
  showUploadList = true,
}) => {
  const handleChange = useCallback(
    (info: UploadChangeParam<UploadFile>) => {
      const sortedFiles = [...info.fileList].sort((a, b) =>
        a.name.localeCompare(b.name)
      )
      onFileListChange(sortedFiles)
    },
    [onFileListChange]
  )

  const beforeUpload = useCallback(
    (file: File) => {
      // if we upload a folder from Mac, it includes .DS_Store files
      if (file.name === '.DS_Store') {
        return Upload.LIST_IGNORE
      }
      return validateFile(file, maxFileSize, acceptedFileTypes)
    },
    [maxFileSize, acceptedFileTypes]
  )

  const uploadProps = useMemo(
    () => ({
      name: 'file',
      multiple,
      directory,
      fileList,
      showUploadList,
      beforeUpload,
      onChange: handleChange,
      disabled,
      onRemove: (file: UploadFile) => {
        const newFileList = fileList.filter((f) => f.uid !== file.uid)
        onFileListChange(newFileList)
        return true
      },
    }),
    [
      multiple,
      directory,
      fileList,
      beforeUpload,
      handleChange,
      disabled,
      onFileListChange,
      showUploadList,
    ]
  )

  return (
    <StyledDragger
      {...uploadProps}
      fileList={[...fileList]}
      $condensed={condensed}>
      {!uploading && (
        <>
          <UploadIcon $condensed={condensed}>
            <UploadOutlined style={{ fontSize: condensed ? '2rem' : '3rem' }} />
          </UploadIcon>
          {!condensed ? (
            <UploadText>
              {directory
                ? 'Click or drag files or folders to this area to upload'
                : 'Click or drag files to this area to upload'}
            </UploadText>
          ) : (
            <UploadText>Upload Files</UploadText>
          )}
          {!condensed && (
            <UploadHint>
              {acceptedFileTypes.includes('*/*')
                ? `Supports all file types. Max ${maxFileSize}MB per file.`
                : `Accepted types: ${acceptedFileTypes.join(', ')}. Max ${maxFileSize}MB per file.`}
            </UploadHint>
          )}
        </>
      )}
    </StyledDragger>
  )
}
